/** SouthIndianTradition — RSVP. */

import React from 'react';
import { motion } from 'framer-motion';
import { Mail, CalendarClock } from 'lucide-react';
import { SOUTH_INDIA_EASE } from '../theme';
import type { SouthIndianContent } from '../content';
import { useRsvp } from '@/src/modules/rsvp';

export function RsvpSection({
  content,
  invitationId,
  isLive = false,
}: {
  content: SouthIndianContent['rsvp'];
  invitationId: string | null;
  isLive?: boolean;
}) {
  const [name, setName] = React.useState('');
  const [attending, setAttending] = React.useState<'yes' | 'no' | ''>('');

  const { state, errorMessage, submit } = useRsvp(invitationId, isLive);
  const sent = state === 'success';
  const sending = state === 'loading';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !attending || sending) return;
    await submit({
      guestName: name.trim(),
      status: attending === 'yes' ? 'ACCEPTED' : 'DECLINED',
      headcount: 1,
    });
  };

  return (
    <section
      id="rsvp"
      className="px-6 py-20 text-center"
      style={{ background: '#f3edc2', color: '#1f1103' }}
    >
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: SOUTH_INDIA_EASE }}
        className="text-sm uppercase tracking-[0.3em]"
        style={{ color: '#843c3a' }}
      >
        {content.label}
      </motion.p>
      <h2 className="mt-2 font-serif text-4xl font-semibold" style={{ color: '#843c3a' }}>
        {content.title}
      </h2>
      <p className="mx-auto mt-4 max-w-xl" style={{ color: '#634f41' }}>
        {content.description}
      </p>

      <div className="mt-6 inline-flex items-center gap-2 text-sm" style={{ color: '#4a7e4d' }}>
        <CalendarClock className="h-4 w-4" />
        Kindly respond by {content.deadline}
      </div>

      {content.externalLink ? (
        <div className="mt-8">
          <a
            href={content.externalLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full px-8 py-3 text-sm font-semibold uppercase tracking-wider text-white shadow-lg transition-transform hover:scale-105"
            style={{ background: '#843c3a' }}
          >
            <Mail className="h-4 w-4" /> RSVP Here
          </a>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mx-auto mt-8 max-w-md space-y-4">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full rounded-lg border px-4 py-3 text-center outline-none focus:border-[#843c3a]"
            style={{ borderColor: 'rgba(132, 60, 58, 0.2)' }}
          />
          <div className="flex justify-center gap-3">
            {(['yes', 'no'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setAttending(opt)}
                className={`rounded-full px-6 py-2 text-sm font-semibold capitalize transition ${
                  attending === opt
                    ? 'text-white'
                    : 'border text-[#634f41]'
                }`}
                style={{
                  background: attending === opt ? '#843c3a' : 'transparent',
                  borderColor: 'rgba(132, 60, 58, 0.3)',
                }}
              >
                {opt === 'yes' ? 'Accepts with pleasure' : 'Declines with regret'}
              </button>
            ))}
          </div>
          {state === 'error' && errorMessage && (
            <p className="text-sm font-medium" style={{ color: '#b3261e' }}>{errorMessage}</p>
          )}
          <button
            type="submit"
            disabled={!name || !attending || sending || sent}
            className="w-full rounded-full px-8 py-3 text-sm font-semibold uppercase tracking-wider shadow-lg transition-transform hover:scale-[1.02] disabled:opacity-40"
            style={{ background: '#cba135', color: '#1f1103' }}
          >
            {sent ? 'Thank you!' : sending ? 'Sending…' : 'Send RSVP'}
          </button>
        </form>
      )}
    </section>
  );
}
